import eventsAPI from "../../../api/eventsAPI";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useEvent } from "../../../context/EventContext";
import { useState } from "react";
import Loader from "../../Loader/Loader";

// * The Stall component is responsible for rendering the form for
//  * adding/editing a stall for the main event. It also renders a
//  * list of existing stalls and the "Add" and "Next" buttons.
const Stall = ({ onNext }) => {
  const {
    stallFormData,
    setStallFormData,
    handleStallChange,
    mainEventID,
    stallAdded,
    setStallAdded,
    setLoading,
    setStallEditIndex,
    stallEditIndex,
    stalls,
    setStalls,
    loading,
    types,
  } = useEvent();

  const [isFormVisible, setIsFormVisible] = useState(false);
  const [verticalLineHeight, setVerticalLineHeight] = useState(0);
  const [horizontalLineWidth, setHorizontalLineWidth] = useState(0);

  //   * Handles the "Add Stall" button click by setting the isFormVisible state to true,
  //   * which displays the stall form.
  const handleAddSpace = () => {
    setIsFormVisible(true);
  };

  //   * Handles the "Edit" button click by setting the stallFormData state to the
  //   * stall details at the given index, setting the isFormVisible state to true,
  //   * and setting the editIndex state to the given index. This displays the stall
  //   * form with the stall details prepopulated.
  const handleEdit = (index) => {
    const stall = stalls[index];

    // Extracting stall length and width from the array
    const stall_size_length = stall.stall_size?.[0]?.stall_length || "";
    const stall_size_width = stall.stall_size?.[1]?.stall_width || "";

    setStallFormData({
      ...stall,
      stall_size_length, // Set the extracted length
      stall_size_width, // Set the extracted width
    });
    setIsFormVisible(true);
    setStallEditIndex(index);
  };

  //   * Handles the "Add Stall" button click by setting the stallFormData state to an
  //   * empty object with mainEventID, setting the isFormVisible state to true, and
  //   * setting the editIndex state to null. This displays the stall form.
  const handleStallAdd = () => {
    setStallFormData({
      stall_name: "",
      stall_type: "",
      registration_type: "",
      stall_size_length: "",
      stall_size_width: "",
      stall_price: "",
      number_of_stalls: "",
      number_of_stall_booked: "",
      mainEventID: mainEventID,
    });
    setIsFormVisible(true);
    setStallEditIndex(null);
  };

  //   * Handles the "Save" button click in the stall form.
  //   * If an image is selected, it uploads the image to Firebase Storage and then sends the image URL in the request.
  //   * If the request is successful, it shows a success message, sets the isFormVisible state to false, and sets the stallAdded state to true.
  //   * It also updates the stalls state with the newly created stall.
  //   * If the editIndex state is not null, it updates the stall at that index in the stalls state.
  //   * Otherwise, it adds the new stall to the stalls state.
  const handleSave = async (e) => {
    e.preventDefault();

    const stallSize = [
      { stall_length: stallFormData.stall_size_length },
      { stall_width: stallFormData.stall_size_width },
    ];
    const updatedFormData = {
      ...stallFormData,
      stall_size: stallSize,
      mainEventID,
    };

    try {
      const response = await eventsAPI.post(
        "/organizer/stall",
        updatedFormData
      );
      if (response.status === 200) {
        toast.success("Stall added successfully!");
        setLoading(false);
        setIsFormVisible(false);
        setStallAdded(true);
        if (stallEditIndex !== null) {
          const updatedStalls = stalls.map((stall, index) =>
            index === stallEditIndex ? response.data : stall
          );
          setStalls(updatedStalls);
        } else {
          setStalls([...stalls, response.data]);
        }
      }
    } catch (error) {
      console.log("Error creating Stall:", error);
      setLoading(false);
    }
  };

  //   * Handles the "Update" button click in the stall form.
  //   * Sends a POST request to the server to update a stall.
  //   * If successful, it shows a success message, sets the isFormVisible state to false, and updates the local state with the updated stall data.
  //   * If the request fails, it shows an error message.
  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await eventsAPI.post(
        "/organizer/updateStall",
        stallFormData
      );

      if (response.status === 200) {
        toast.success("stall updated successfully!");
        setLoading(false);
        setIsFormVisible(false);

        // Update the local state with the updated location data
        const updatedStalls = stalls.map((stall, index) =>
          index === stallEditIndex ? response.data : stall
        );
        setStalls(updatedStalls);
      }
    } catch (error) {
      console.log("Error updating Location:", error);
      setLoading(false);
    }
  };

  //    * Handles the deletion of a stall by sending a DELETE request to the server.
  //    * If successful, it removes the stall from the local state and shows a success message.
  //    * If the request fails, it shows an error message.
  const handleDelete = async (e, id) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await eventsAPI.delete(`/organizer/stall/${id}`); // Pass the location's id

      if (response.status === 200) {
        toast.success("Stall deleted successfully!");
        setLoading(false);
        setIsFormVisible(false);

        // Remove the deleted location from the local state
        const updatedStall = stalls.filter((stall) => stall._id !== id);
        setStalls(updatedStall);
      }
    } catch (error) {
      console.log("Error deleting Stall:", error);
      setLoading(false);
    }
  };

  const calculateDimension = () => {
    const length = parseFloat(stallFormData.stall_size_length) || 0;
    const width = parseFloat(stallFormData.stall_size_width) || 0;

    // Assuming maxHeight is the maximum height for the vertical line
    const maxHeight = 100; // You can adjust this value as needed
    const maxWidth = 100;

    if (length > width) {
      // Calculate the percentage based on length and width
      const percentage = (length / (length + width)) * 100;

      // Set the heights and widths
      setVerticalLineHeight(maxHeight); // Set vertical line to 100%
      setHorizontalLineWidth((percentage / 100) * maxHeight); // Set horizontal line based on percentage
    } else if (width > length) {
      const percentage = (width / (length + width)) * 100;

      setHorizontalLineWidth(maxWidth); // Set Horizontal line to 100%
      setVerticalLineHeight((percentage / 100) * maxWidth); // Set horizontal line based on percentage
    } else {
      // If length is not greater than width, you might want to reset the lines
      setVerticalLineHeight(0); // Reset vertical line height if condition is not met
      setHorizontalLineWidth(0); // Reset horizontal line width
    }
  };

  const totalArea = () => {
    const length = parseFloat(stallFormData.stall_size_length) || 0;
    const width = parseFloat(stallFormData.stall_size_width) || 0;
    const area = length * width;
    return area.toFixed(2);
  };

  // Handle changes in stall size inputs
  const handleSizeChange = (e) => {
    handleStallChange(e); // Update stallFormData state
    calculateDimension();
    totalArea();
  };

  return (
    <div className="stall-section">
      {/* Show "Add Stall" button if no stalls are added yet */}
      {!isFormVisible && !stallAdded && (
        <button className="add-space" onClick={handleAddSpace}>
          Add Stall
        </button>
      )}

      {/* Show existing stalls and the "Add" and "Next" buttons */}
      {!isFormVisible && stallAdded && (
        <>
          {stalls.map((stall, index) => (
            <div className="saved-details" key={index}>
              <p>
                <span>Stall Name: </span> {stall.stall_name}
              </p>
              <p>
                <span>Stall Type:</span> {stall.stall_type}
              </p>
              <button className="add-space" onClick={() => handleEdit(index)}>
                Edit
              </button>
            </div>
          ))}
          <div className="buttons-controller">
            <button className="add-space" onClick={handleStallAdd}>
              Add
            </button>
            <button className="add-space" onClick={onNext}>
              Next
            </button>
          </div>
        </>
      )}

      {/* Stall Form */}
      {isFormVisible && (
        <div className="stall-form-visual-section">
          <form>
            <h2>Stall Booking</h2>
            <div className="stall-conference-details">
              <label htmlFor="stall-name">Stall Name</label>
              <input
                value={stallFormData.stall_name}
                onChange={handleStallChange}
                name="stall_name"
                type="text"
                placeholder="Enter the name of your stall"
              />
              <label htmlFor="stall-type">Stall Type</label>
              <select
                name="stall_type"
                value={stallFormData.stall_type}
                onChange={handleStallChange}
              >
                <option value="select">select</option>
                <option value="it">IT</option>
                <option value="mechanical">Mechanical</option>
                <option value="electrical">Electrical</option>
              </select>
              <label htmlFor="registration-type">Registration Type</label>
              <select
                name="registration_type"
                value={stallFormData.registration_type}
                onChange={handleStallChange}
              >
                <option value="select">Select</option>
                {types.map((type) => (
                  <option key={type._id} value={type.typename}>
                    {type.typeName}
                  </option>
                ))}
              </select>
              <label htmlFor="stall-size">Stall length</label>
              <div className="stall-size-inputs">
                <input
                  name="stall_size_length"
                  type="number"
                  placeholder="Height"
                  value={stallFormData.stall_size_length || ""}
                  onChange={handleSizeChange}
                />
                <label htmlFor="stall-size"> Stall Width</label>
                <input
                  name="stall_size_width"
                  type="number"
                  placeholder="Width"
                  value={stallFormData.stall_size_width || ""}
                  onChange={handleSizeChange}
                />
              </div>
              <label htmlFor="stall-price">Stall Price</label>
              <input
                name="stall_price"
                type="number"
                placeholder="Stall Price"
                value={stallFormData.stall_price || ""}
                onChange={handleStallChange}
              />
              <label htmlFor="stall-price">Total Stall</label>
              <input
                name="number_of_stalls"
                type="number"
                placeholder="Total Stalls"
                value={stallFormData.number_of_stalls || ""}
                onChange={handleStallChange}
              />
              <label htmlFor="stall-price">Number Of Stall Booked</label>
              <input
                name="number_of_stall_booked"
                type="number"
                placeholder="Number Of Stall Booked"
                value={stallFormData.number_of_stall_booked || ""}
                onChange={handleStallChange}
              />

              {/* Show the "Save" button if adding a new location, and "Update" button if editing */}
              {stallEditIndex === null ? (
                <button
                  type="submit"
                  className="create-button"
                  onClick={handleSave}
                >
                  {loading ? <Loader /> : "SAVE"}
                </button>
              ) : (
                <>
                  <button
                    type="submit"
                    className="create-button"
                    onClick={handleUpdate}
                  >
                    {loading ? <Loader /> : "UPDATE"}
                  </button>
                  <button
                    type="submit"
                    className="create-button"
                    onClick={(e) => handleDelete(e, stallFormData._id)}
                  >
                    {loading ? <Loader /> : "DELETE"}
                  </button>
                </>
              )}
            </div>
          </form>
          <div className="graph">
            <div
              className="vertical-line"
              style={{
                height: `${verticalLineHeight}%`,
                // height: "120px",
                transition: "height 1s ease",
              }}
            ></div>
            <div
              className="horizontal-line"
              style={{
                width: `${horizontalLineWidth}%`,
                // width: "100px",
                transition: "width 1s ease",
              }}
            ></div>
            <p className="vertical-label">{stallFormData.stall_size_length}m</p>
            <p className="horizontal-label">
              {stallFormData.stall_size_width}m
            </p>
            <p className="stall-area">
              {totalArea()}m<sup>2</sup>
            </p>
            {/* <button className="add-space" onClick={() => calculateDimension()}>See View</button> */}
          </div>
        </div>
      )}
    </div>
  );
};

export default Stall;
